import React from 'react';
import { Button, Select, MenuItem } from '@mui/material';
import styled from '@emotion/styled';

import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';

import useIsMobile from '../../hooks/useIsMobile';
import { BasicFlex } from '../Common';

import { getViewableRows } from '../util';

const Footer = styled(BasicFlex)`
  padding: 1rem;
  /* box-shadow: ${props => props.theme.shadows[1]}; */
  border-radius: 5px;
  border-top-right-radius: 2px;
  border-top-left-radius: 2px;
  margin: .2rem 0;
  background-color: ${props => props.theme.colors.darkBg};
  align-items: center;
`;

const TotalShowing = styled(BasicFlex)`
  padding: .5rem;
  .stat {
    font-weight: 700;
    /* color: ${props => props.theme.colors.primary}; */
  }
`;

const PageButton = styled(Button)`
  min-width: auto;
  padding: .2rem .8rem;
`;

const ArrowButton = styled(Button)`
  min-width: auto;
  padding: .2rem .3rem;
`;

const PageRowsMenu = styled(Select)`
  background-color: #FFF;
  padding: 0;
  min-width: 8rem;
`;

const getPageNumbers = (isMobile, currentPage, totalPages) => {
    // Mobile view - show only 2 previous numbers
    if (isMobile) {
      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }
      
      if (currentPage <= 2) {
        return [1, 2, 3, '...', totalPages];
      }
      
      if (currentPage >= totalPages - 2) {
        return [1, '...', totalPages - 2, totalPages - 1, totalPages];
      }
      
      return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
    }
    
    // Desktop view - original logic
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 3) {
      return [1, 2, 3, 4, 5, '...', totalPages - 1, totalPages];
    }

    if (currentPage >= totalPages - 2) {
      return [1, 2, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, 2, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages - 1, totalPages];
  };

const SimplePagination = ({ 
  currentPage = 1,
  resultCount = 0,
  rowsPerPage = 5,
  results = [],
  customRPP = [],
  setPagination = () => {}
}) => {
  const { isMobile } = useIsMobile() || false;

   const onPageChange = (newPage) => {
    setPagination(x => ({...x, page: newPage}));
  }

  const handlePageRows = (e) => {
    setPagination(x => ({...x, rowsPerPage: e.target.value}));
  }

  const totalPages = Math.floor(resultCount / rowsPerPage) || 0;

  return (
    <Footer dir={isMobile ? "column" : "row"} gap={isMobile ? "1rem" : "0"}>
      <TotalShowing justify={isMobile ? "center" : "flex-start"} gap="1rem">
        Showing: <span className='stat'> {getViewableRows(results, currentPage, rowsPerPage)} </span> of {resultCount}  &nbsp; | &nbsp; {totalPages + 1} pages
      </TotalShowing>
      <BasicFlex justify={isMobile ? "center" : "flex-end"} gap="3rem">
        <BasicFlex align="center" gap="4px" justify="flex-start" width="auto">
          <ArrowButton
            color="basic"
            variant="outlined"
            size="small"
            onClick={() => onPageChange(0)}
            disabled={currentPage === 0}
          >
            <KeyboardDoubleArrowLeftRoundedIcon />
          </ArrowButton>
          
          <ArrowButton
            color="basic"
            variant="outlined"
            size="small"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            <KeyboardArrowLeftRoundedIcon />
          </ArrowButton>

          {getPageNumbers(isMobile, currentPage, totalPages).map((page, index) => (
            <PageButton
              color="basic"
              key={index}
              variant={page === (currentPage + 1) ? "contained" : "outlined"}
              size="small"
              onClick={() => typeof page === 'number' ? onPageChange(page - 1) : null}
              disabled={page === '...'}
            >
              {page}
            </PageButton>
          ))}

          <ArrowButton
            color="basic"
            variant="outlined"
            size="small"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <KeyboardArrowRightRoundedIcon />
          </ArrowButton>

          <ArrowButton
            color="basic"
            variant="outlined"
            size="small"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
          >
            <KeyboardDoubleArrowRightRoundedIcon />
          </ArrowButton>
        </BasicFlex>
        <PageRowsMenu size="small" 
          value={rowsPerPage} 
          onChange={(e => handlePageRows(e))}
        >
          <MenuItem value={5}>5</MenuItem>
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={15}>15</MenuItem>
          <MenuItem value={20}>20</MenuItem>
          <MenuItem value={25}>25</MenuItem>
          {customRPP?.length ? customRPP.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>) : null}
        </PageRowsMenu>
      </BasicFlex>
    </Footer>
  );
};

export default SimplePagination;